import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PublicLayout } from "@/components/public/PublicLayout";
import { WelcomeScreen } from "@/components/public/WelcomeScreen";
import api from "@/lib/api";
import { SERVICES, HOW_IT_WORKS, MENU_PREVIEW, GALLERY, IMAGES, ALCOHOL_DISCLAIMER, BRAND, resolveImg } from "@/lib/constants";
import { ArrowRight, Check, Sparkles, GlassWater, Leaf } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const DEFAULT_SHOWCASE = [
  { img: "/photos/setup.jpg", label: "Our mobile bar, on location" },
  { img: "/photos/service.jpg", label: "Crafted fresh to order" },
  { img: "/photos/guests.jpg", label: "Guests love the experience" },
];

export default function Home() {
  const [gallery, setGallery] = useState(GALLERY);
  const [heroImg, setHeroImg] = useState(IMAGES.hero);
  const [showcase, setShowcase] = useState(DEFAULT_SHOWCASE);
  const [menuImg, setMenuImg] = useState(IMAGES.cocktail);
  useEffect(() => {
    api.get("/public/gallery").then((r) => {
      const items = r.data || [];
      const byRole = (role) => items.filter((g) => (g.role || "gallery") === role);
      const hero = byRole("hero")[0];
      if (hero) setHeroImg(resolveImg(hero.url));
      const sc = byRole("showcase");
      if (sc.length) setShowcase(sc.slice(0, 3).map((g) => ({ img: resolveImg(g.url), label: g.caption || g.category })));
      const gal = byRole("gallery");
      if (gal.length) setGallery(gal.map((g) => ({ label: g.caption || g.category, img: resolveImg(g.url) })));
      const menu = byRole("menu")[0];
      if (menu) setMenuImg(resolveImg(menu.url));
    }).catch(() => {});
  }, []);
  return (
    <>
      <WelcomeScreen />
      <PublicLayout>
      {/* HERO */}
      <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <img src={heroImg} alt="Mobile bar" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/60 to-ink/30" />
        <div className="df-container relative z-10 text-white py-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow text-olive mb-5">
            {BRAND.location}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-3xl"
          >
            Book Your Mobile Bar Experience
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-6 text-lg text-white/80 max-w-xl"
          >
            Premium cocktails & mocktails, expertly crafted and served at your event. Custom menus, elegant setup, fresh flavors and professional service — brought to you across the GTA.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/quote" className="btn-pill bg-white text-ink hover:bg-white/90 hover:-translate-y-0.5 hover:shadow-xl" data-testid="hero-request-quote">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#services" className="btn-pill border border-white/50 text-white hover:bg-white hover:text-ink" data-testid="hero-view-services">
              View Services
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.6 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70"
          >
            {["Premium setup", "Fresh flavors", "Professional service", "Custom menus"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-olive" /> {t}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 lg:py-32 df-container">
        <Reveal className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">What we do</p>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight">A complete bar experience, tailored to your event</h2>
          <p className="mt-5 text-muted-foreground text-lg">
            Choose up to <strong>3 main cocktails or mocktails</strong>. When possible, the same 3 can be made as mocktails too — giving you up to <strong>6 menu options</strong>. Beer, wine, champagne, prosecco, juices, sodas and simple drinks can be added without extra service charge when we only serve or use ingredients you already have.
          </p>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.06}>
              <div className="df-card p-7 h-full group">
                <div className="h-11 w-11 rounded-full bg-clay flex items-center justify-center mb-5 group-hover:bg-olive/20 transition-colors">
                  <GlassWater className="h-5 w-5 text-olive" />
                </div>
                <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REAL PHOTOS SHOWCASE */}
      <section className="pb-6 df-container">
        <Reveal>
          <div className="grid sm:grid-cols-3 gap-4">
            {showcase.map((s, i) => (
              <div key={s.label + i} className="relative overflow-hidden rounded-2xl h-72 group">
                <img src={s.img} alt={s.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                <span className="absolute bottom-4 left-4 text-white font-serif text-xl">{s.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 lg:py-32 bg-sand">
        <div className="df-container">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow mb-3">Simple & seamless</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight">How it works</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.step} delay={(i % 4) * 0.06}>
                <div className="relative pl-4 border-l-2 border-olive/30 h-full">
                  <div className="font-serif text-5xl text-olive/40 mb-2">{String(s.step).padStart(2, "0")}</div>
                  <h3 className="font-semibold text-lg mb-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MENU PREVIEW */}
      <section id="menu" className="py-24 lg:py-32 df-container">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <Reveal className="lg:col-span-2 lg:sticky lg:top-28">
            <p className="eyebrow mb-3">The drinks</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-5">Menu preview</h2>
            <p className="text-muted-foreground mb-6">A taste of the signature cocktails and mocktails we craft. Every menu is fully customized for your event.</p>
            <img src={menuImg} alt="Craft cocktail" className="rounded-2xl w-full h-72 object-cover" />
            <p className="mt-3 text-xs text-muted-foreground flex items-center gap-2"><Leaf className="h-3 w-3 text-olive" /> Served in premium clear 12 oz cups — glassware available for corporate events.</p>
          </Reveal>
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
            {MENU_PREVIEW.map((m, i) => (
              <Reveal key={m.name} delay={(i % 2) * 0.05}>
                <div className="df-card p-5 h-full">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-serif text-xl">{m.name}</h3>
                    <span className="text-[10px] uppercase tracking-widest text-olive">{m.tag}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 lg:py-32 bg-ink text-white">
        <div className="df-container">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow text-olive mb-3">The experience</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight">Gallery</h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((g, i) => (
              <Reveal key={g.label} delay={(i % 3) * 0.05}>
                <div className="relative group overflow-hidden rounded-2xl h-72">
                  <img src={g.img} alt={g.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
                  <span className="absolute bottom-5 left-5 font-serif text-2xl">{g.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 df-container">
        <Reveal>
          <div className="rounded-3xl bg-sand border border-line p-10 lg:p-16 text-center">
            <Sparkles className="h-8 w-8 text-olive mx-auto mb-5" />
            <h2 className="font-serif text-4xl lg:text-5xl mb-4">Let's craft your event</h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">Tell us about your celebration and receive a professional, tailored quote. A 50% deposit reserves your date.</p>
            <Link to="/quote" className="btn-dark inline-flex" data-testid="cta-request-quote">Request a Quote <ArrowRight className="h-4 w-4" /></Link>
            <p className="mt-8 text-xs text-muted-foreground max-w-2xl mx-auto">{ALCOHOL_DISCLAIMER}</p>
          </div>
        </Reveal>
      </section>
      </PublicLayout>
    </>
  );
}
